import API_ROUTES, { BASE_URL, FORM_BUILDER_PATH } from "../constants/api";
import HttpUtil from "../http-handler/http-util";
import { DEFAULT_ITEMS_PER_PAGE, LS_COMPANY_INFO_KEY, SORT_ORDER_DESC } from "../constants/common";

export default class FormService {
  /**
   * When company reporting is financial year (FY), assessments/v2 expects FY context query params.
   * Reads `dataFrequencyType` (and FY month fields) from localStorage `companyInfo` (same shape as CompanySettings persists).
   * @returns {string} suffix starting with & or empty string
   */
  static getFinancialYearQuerySuffixForAssessmentsV2() {
    if (typeof localStorage === "undefined") return "";
    try {
      const raw = localStorage.getItem(LS_COMPANY_INFO_KEY);
      if (!raw) return "";
      const companyInfo = JSON.parse(raw);
      if (companyInfo?.dataFrequencyType !== "FY") return "";
      const financialYearStart = companyInfo?.financialYearStart ?? "APRIL";
      const financialYearEnd = companyInfo?.financialYearEnd ?? "MARCH";
      const qs = new URLSearchParams({
        dataFrequencyType: "FY",
        financialYearStart,
        financialYearEnd,
      });
      return `&${qs.toString()}`;
    } catch {
      return "";
    }
  }

  static listForms(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    regulationId = "",
    selfAssessment = false
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LISTFORMS.replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace(key ? "<key>" : "&key=<key>", key)
        .replace("<sort>", sort)
        .replace(
          regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
          regulationId
        )
        .replace("<selfAssessment>", selfAssessment)
    );
  }

  static shareForms(params) {
    return HttpUtil.patch(API_ROUTES.FORMS.SHAREFORMS, params);
  }
  static listFormsForSharing(
    regulationId = "",
    status = "PUBLISHED",
    selfAssessment = false
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LISTFORMSFORSHARING.replace(
        regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
        regulationId
      )
        .replace("<status>", status)
        .replace("<selfAssessment>", selfAssessment)
    );
  }
  static listFormsForSubmission(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    supplierId,
    companyId = "",
    regulationId = ""
  ) {
    if (companyId) {
      return HttpUtil.get(
        API_ROUTES.FORMS.LISTFORMSFORSUBMISSION.replace(
          "<supplierId>",
          supplierId
        )
          .replace(
            companyId ? "<companyId>" : "?companyId=<companyId>",
            companyId
          )
          .replace(
            regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
            regulationId
          )
          .replace("<pageNumber>", pageNumber)
          .replace("<pageSize>", pageSize)
          .replace("<sort>", sort)
          .replace(key ? "<key>" : "&key=<key>", key)
      );
    } else {
      return HttpUtil.get(
        API_ROUTES.FORMS.LISTALLFORMSFORSUBMISSION.replace(
          "<supplierId>",
          supplierId
        )
          .replace(
            regulationId ? "<regulationId>" : "?regulationId=<regulationId>",
            regulationId
          )
          .replace("<pageNumber>", pageNumber)
          .replace("<pageSize>", pageSize)
          .replace(sort ? "<sort>" : "&sort=<sort>", sort)
          .replace(key ? "<key>" : "&key=<key>", key)
      );
    }
  }
  static getSubmissionToken(id) {
    return HttpUtil.get(
      API_ROUTES.FORMS.FORMSUBMISSIONTOKEN.replace("<formId>", id)
    );
  }
  static publishForms(id) {
    return HttpUtil.patch(
      API_ROUTES.FORMS.PUBLISHFORMS.replace("<formId>", id)
    );
  }

  static deleteForm(formId) {
    return HttpUtil.delete(
      API_ROUTES.FORMS.DELETE.replace("<formId>", formId)
    );
  }
  static listSubmittedForms(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    formId
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LISTSUBMITTEDFORMS.replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace(key ? "<key>" : "&key=<key>", key)
        .replace("<sort>", sort)
        .replace("<formId>", formId)
    );
  }
  static listSubmittedFormsSupplier(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    formId,
    token
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LISTSUBMITTEDFORMSSUPPLIER.replace(
        "<pageNumber>",
        pageNumber
      )
        .replace("<pageSize>", pageSize)
        .replace(key ? "<key>" : "&key=<key>", key)
        .replace("<sort>", sort)
        .replace("<formId>", formId)
        .replace("<token>", token)
    );
  }
  static listFormStatusSupplier(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    encodedUrlParam
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LISTFORMSSTATUSSUPPLIER.replace(
        "<pageNumber>",
        pageNumber
      )
        .replace("<pageSize>", pageSize)
        .replace(key ? "<key>" : "&key=<key>", key)
        .replace("<sort>", sort)
        .replace("<encodedUrlParam>", encodedUrlParam)
    );
  }
  static getFileUploadFields(id) {
    return HttpUtil.get(
      API_ROUTES.FORMS.GETFILEUPLOADFIELDS.replace("<formId>", id)
    );
  }
  static updateFileFieldsToDocs(id, fieldId, docTypeId) {
    return HttpUtil.patch(
      API_ROUTES.FORMS.UPDATEFILEFIELDTODOC.replace("<formId>", id)
        .replace("<fieldId>", fieldId)
        .replace("<docTypeId>", docTypeId)
    );
  }
  static getLanguages() {
    return HttpUtil.get(API_ROUTES.FORMS.GETLANGUAGES);
  }
  static createForm(params) {
    return HttpUtil.post(API_ROUTES.FORMS.CREATEFORM, params);
  }
  
  static listOpenFormsForSupplier(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    sort,
    supplierId,
    companyId = 0,
    regulationId = 0
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LIST_OPEN_FORM_SUBMISSIONS_BY_SUPPLIER      
      .replace("<supplierId>", supplierId)
      .replace(
        regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
        regulationId
      )
      .replace(companyId ? "<companyId>" : "&companyId=<companyId>",companyId)
      .replace("<pageNumber>",pageNumber)
      .replace("<pageSize>", pageSize)
      .replace("<sort>", sort)
    );
  }

  static getSharesforForm(formId) {
    return HttpUtil.get(
      API_ROUTES.FORMS.GET_SHARES_FOR_FORM.replace("<formId>", formId)
    );
  }
  static shareSelfAssessmentForm(params) {
    return HttpUtil.patch(API_ROUTES.FORMS.SHARE_SELF_ASSESSMENT_FORM, params);
  }

  static listSubmittedAssessmentForms(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    formId,
    period,
    sort,
    selfAssessment = false,
    supplierId = 0,
    branchId = 0,
    companyId = 0,
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.LIST_SUBMITTED_FORMS_V2
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize)
      .replace(key ? "<key>" : "&key=<key>", key)
      .replace("<formId>", formId)
      .replace(period ? "<period>" : "&period=<period>", period ? period : '')
      .replace(sort ? "<sort>" : "&sort=<sort>", sort ? sort : '')
      .replace("<selfAssessment>", selfAssessment)
      .replace(supplierId ? "<supplierId>" : "&supplierId=<supplierId>", supplierId ? supplierId : '')
      .replace(branchId ? "<branchId>" : "&branchId=<branchId>", branchId ? branchId : '')
      .replace(companyId ? "<companyId>" : "&companyId=<companyId>", companyId ? companyId : '')
    );
  }

  static getSubmittedAssessmentFormsCount(
    formId,
    period,
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.GET_SUBMITTED_FORMS_COUNT
      .replace("<formId>", formId)
      .replace("<period>", period)      
    );
  }

  static getAssessmentForm(
    formId
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.GET_ASSESSMENT_FORM
      .replace("<formId>", formId)    
    );
  }


  static remindFormSubmission(params) {
    return HttpUtil.patch(API_ROUTES.FORMS.REMIND_FORM_SUBMISSION, params);
  }

  static createAssessment(payload) {
    return HttpUtil.post(API_ROUTES.ASSESSMENTS.SAVE, payload);
  }

  static getFormPreview(id, noCache = false) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_FORM_PREVIEW
      .replace('<formId>', id)
      .replace('<no-cache>', noCache)
    );
  }

  static listGroups() {
    return HttpUtil.get(API_ROUTES.ASSESSMENTS.LIST_GROUPS + '?enabled=true');
  }

  static listGroupsPaginated(pageNumber = 0, pageSize = DEFAULT_ITEMS_PER_PAGE, enabled = undefined, sort = 'lastModifiedDate,desc') {
    let url = API_ROUTES.ASSESSMENTS.LIST_GROUPS_PAGINATED
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize)
      .replace("<sort>", sort);
    
    if (enabled !== undefined) {
      url += `&enabled=${enabled}`;
    }
    
    return HttpUtil.get(url);
  }

  static createGroup(payload) {
    return HttpUtil.post(API_ROUTES.ASSESSMENTS.CREATE_GROUP, payload);
  }

  static getForms(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    regulationId = "",
    selfAssessment = false,
    fetchFieldSectionCounts = false,
    withLogo = false,
    frequency = null,
    type =null
  ) {
    return HttpUtil.get(
      API_ROUTES.FORMS.GET_ALL
        .replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace(key ? "<key>" : "&key=<key>", key)
        .replace("<sort>", sort)
        .replace(
          regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
          regulationId
        )
        .replace("<selfAssessment>", selfAssessment)
        .replace("<fetchFieldSectionCounts>", fetchFieldSectionCounts)
        .replace("<withLogo>", withLogo)
        .replace(frequency ? "<frequency>" : "&frequency=<frequency>", frequency ?? '')
        .replace(type ? "<type>" : "&type=<type>", type ?? '')
    );
  }

  static createNewForm(params) {
    return HttpUtil.post(API_ROUTES.FORMS.CREATE, params);
  }

  static listAssessments(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    key = "",
    sort,
    regulationId = "",
    selfAssessment = false,
    fetchFieldSectionCounts = false,
    type = '',
    year
  ) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.LIST.replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace(key ? "<name>" : "&name=<name>", key)        
        .replace("<sort>", sort)
        .replace(
          regulationId ? "<regulationId>" : "&regulationId=<regulationId>",
          regulationId
        )
        .replace("<selfAssessment>", selfAssessment)
        .replace(
          fetchFieldSectionCounts ? "<fetchFieldSectionCounts>" : "&fetchFieldSectionCounts=<fetchFieldSectionCounts>",
          fetchFieldSectionCounts
        )
        .replace(type ? "<type>" : "&type=<type>",type)  
        .replace("&year=<year>", year === null ? "" : "&year="+year)    
    );
    
  }

  /**
   * List assessments (v2). Use year for calendar year (CY); use periodStart+periodEnd for financial year (FY).
   * periodStart and periodEnd are optional; when omitted (or either is null), the request uses year as before.
   * Other modules (e.g. QuantitativeDCEList) that call with only (..., year) are unchanged and still get ?year=...
   */
  static listActiveAssessments(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    sort = 'reportingPeriod,desc',
    type = 'GENERAL',
    selfAssessment = true,
    submissionStatus = null,
    fetchFieldSectionCounts = true,
    year = null,
    periodStart = null, // optional: when both periodStart and periodEnd are set (FY), used instead of year
    periodEnd = null    // optional: when both set, URL has periodStart/periodEnd; otherwise year as before
  ) {
    const usePeriod = Boolean(periodStart && periodEnd);
    const baseTemplate = usePeriod ? API_ROUTES.ASSESSMENTS.LIST_V2_PERIOD : API_ROUTES.ASSESSMENTS.LIST_V2;
    let apiUrl = baseTemplate
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize)
      .replace("<sort>", sort)
      .replace("<type>", type)
      .replace("<selfAssessment>", selfAssessment)
      .replace("<fetchFieldSectionCounts>", fetchFieldSectionCounts);
    
    // Handle submissionStatus - if null, remove the parameter
    if (submissionStatus) {
      apiUrl = apiUrl.replace("<submissionStatus>", submissionStatus);
    } else {
      apiUrl = apiUrl.replace("&submissionStatus=<submissionStatus>", "");
    }
    
    if (usePeriod) {
      apiUrl = apiUrl.replace("<periodStart>", periodStart).replace("<periodEnd>", periodEnd);
    } else {
      // Handle year - if null, remove the parameter (CY or null)
      if (year) {
        apiUrl = apiUrl.replace("<year>", year);
      } else {
        apiUrl = apiUrl.replace("&year=<year>", "");
      }
    }

    // FY extras only when scoped by year or by period (not unfiltered lists); suffix is empty unless company is FY
    if (year || usePeriod) {
      apiUrl += FormService.getFinancialYearQuerySuffixForAssessmentsV2();
    }

    return HttpUtil.get(apiUrl);
  }

  static shareAssessment(id) {
    return HttpUtil.patch(API_ROUTES.ASSESSMENTS.SHARE.replace('<id>', id));
  }

  static updateGroup(id, payload) {
    return HttpUtil.put(API_ROUTES.ASSESSMENTS.UPDATE_GROUP.replace('<groupId>', id), payload);
  }

  static deleteGroup(id) {
    return HttpUtil.delete(API_ROUTES.ASSESSMENTS.DELETE_GROUP.replace('<groupId>', id));
  }

  /**
   * @param {string|number} id - Assessment id
   * @param {{ fetchAll?: boolean }} [options] - When `fetchAll` is true, adds `?fetchAll=true` (full reviewer/approver levels per branch).
   */
  static getAssessment(id, options = {}) {
    const config = {};
    if (options.fetchAll === true) {
      config.params = { fetchAll: true };
    }
    return HttpUtil.get(API_ROUTES.ASSESSMENTS.GET.replace('<id>', id), config);
  }
 

  static updateAssessment(id, payload) {
   
    return HttpUtil.put(
      API_ROUTES.ASSESSMENTS.UPDATE.replace('<id>', id), 
      payload
    );
}


  static searchAssessment(formId, period) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_FOR_PERIOD
      .replace('<formId>', formId)
      .replace('<period>', period)
    );
  }

  static getSubmissions(
    assessmentId,
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    sort,
    key = "",
    toReview = false,
    status = null
  ) {
    let apiUrl = API_ROUTES.ASSESSMENTS.GET_SUBMISSIONS
      .replace("<id>", assessmentId)
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize)
      .replace("<sort>", sort)
      .replace(key ? "<key>" : "&key=<key>", key);
    
    // Handle toReview parameter - if false, remove the parameter
    if (toReview) {
      apiUrl = apiUrl.replace("<toReview>", "true");
    } else {
      apiUrl = apiUrl.replace("&toReview=<toReview>", "");
    }
    
    // Handle status parameter
    if (status) {
      apiUrl = apiUrl.replace("<status>", status);
    } else {
      apiUrl = apiUrl.replace("&status=<status>", "");
    }
    
    return HttpUtil.get(apiUrl);
  }

  static getBranchSubmissions(
    assessmentId,
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    status = null
  ) {
    let apiUrl = API_ROUTES.ASSESSMENTS.GET_BRANCH_SUBMISSIONS
      .replace("<id>", assessmentId)
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize);
    
    // Handle status parameter
    if (status) {
      apiUrl = apiUrl.replace("<status>", status);
    } else {
      apiUrl = apiUrl.replace("&status=<status>", "");
    }
    
    return HttpUtil.get(apiUrl);
  }

  static getFormDataLogs(submissionId) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_FORM_DATA_LOGS
        .replace("<submissionId>", submissionId)
    );
  }

  static createNewFormWithLogo(formData, formId = 0) {
    return HttpUtil.post(
      API_ROUTES.FORMS.CREATE_WITH_LOGO.replace('<formId>', formId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  static getLogo(id, formId) {
    return (
      API_ROUTES.FORMS.GET_LOGO
      .replace("<formId>", formId)
      .replace("<id>", id)
    );
  }

  static getSupplierSubmissions(
    pageNumber = 0,
    pageSize = DEFAULT_ITEMS_PER_PAGE,
    sort
  ) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_SUPPLIER_SUBMISSIONS
        .replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace("<sort>", sort)
    );
  }

  static updateDataApproval(status, formId) {
    return HttpUtil.patch(
      API_ROUTES.FORMS.UPDATE_DATA_APPROVAL
      .replace('<formId>', formId)
      .replace('<status>', status),
      {}
    );
  }

  static shareAssessmentSubmission(id, payload) {
    return HttpUtil.patch(API_ROUTES.ASSESSMENTS.SHARE.replace('<id>', id), payload);
  }

  static getSharedBranchesForAssessment(id) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_SHARED_BRANCEHS
      .replace('<id>', id)
    );
  }

  static reshareAssessment(id, payload) {
    return HttpUtil.patch(API_ROUTES.ASSESSMENTS.RE_SHARE.replace('<id>', id), payload);
  }

  static sendReminder(id, payload = null) {
    return HttpUtil.patch(API_ROUTES.ASSESSMENTS.SEND_REMINDER.replace('<id>', id), payload);
  }

  static updateForm(formId, params) {
    return HttpUtil.put(API_ROUTES.FORMS.UPDATE.replace('<formId>', formId), params);
  }

  static downloadFormData(
    assessmentId,
    pageNumber = 0,
    pageSize = 10,
    sort = 'parentBranch,asc',
    fetchAll = true
  ) {
    let apiUrl = API_ROUTES.ASSESSMENTS.GET_SUBMISSIONS
      .replace("<id>", assessmentId)
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize)
      .replace("<sort>", sort)
      .replace("&key=<key>", "");
    
    // Remove toReview and status parameters for download
    apiUrl = apiUrl.replace("&toReview=<toReview>", "");
    apiUrl = apiUrl.replace("&status=<status>", "");
    
    // Add fetchAll parameter
    apiUrl += `&fetchAll=${fetchAll}`;
    
    return HttpUtil.get(apiUrl);
  }

  static exportGroupsExcel(enabled = undefined, sort = undefined) {
    let url = API_ROUTES.ASSESSMENTS.EXPORT_GROUPS_EXCEL;
    const params = [];
    
    if (enabled !== undefined) {
      params.push(`enabled=${enabled}`);
    }
    
    if (sort) {
      params.push(`sort=${sort}`);
    }
    
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    
    return HttpUtil.getExcelData(url);
  }

  static downloadGroupsTemplate() {
    return HttpUtil.getExcelData(API_ROUTES.ASSESSMENTS.EXPORT_GROUPS_TEMPLATE);
  }

  static importGroups(formData) {
    return HttpUtil.post(API_ROUTES.ASSESSMENTS.IMPORT_GROUPS, formData);
  }

  static revokeSubmission(submissionId) {
    return HttpUtil.post(
      API_ROUTES.ASSESSMENTS.REVOKE_FORM_SUBMISSION
        .replace("<submissionId>", submissionId),
      {}
    );
  }

  static getBranchSubmissionsSummary(periodStart, periodEnd, pageNumber = 0, pageSize = 20) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_BRANCH_SUBMISSIONS_SUMMARY
        .replace("<periodStart>", periodStart)
        .replace("<periodEnd>", periodEnd)
        .replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
    );
  }

  static getBranchSubmissionsFrequency(periodStart, periodEnd) {
    return HttpUtil.get(
      API_ROUTES.ASSESSMENTS.GET_BRANCH_SUBMISSIONS_FREQUENCY
        .replace("<periodStart>", periodStart)
        .replace("<periodEnd>", periodEnd)
    );
  }

  static getBranchSubmissionsByStatus(periodStart, periodEnd, status = null) {
    let apiUrl = API_ROUTES.ASSESSMENTS.GET_BRANCH_SUBMISSIONS_BY_STATUS
      .replace("<periodStart>", periodStart)
      .replace("<periodEnd>", periodEnd);

    if (status) {
      apiUrl = apiUrl.replace("<status>", status);
    } else {
      apiUrl = apiUrl.replace("&status=<status>", "");
    }

    return HttpUtil.get(apiUrl);
  }

  static getBranchSubmissionsByFrequencyAndStatus(periodStart, periodEnd, frequency, status, pageNumber = 0, pageSize = 20, reportingPeriod = null) {
    let apiUrl = API_ROUTES.ASSESSMENTS.GET_BRANCH_SUBMISSIONS_BY_FREQUENCY_STATUS
      .replace("<periodStart>", periodStart)
      .replace("<periodEnd>", periodEnd)
      .replace("<frequency>", frequency)
      .replace("<status>", status)
      .replace("<pageNumber>", pageNumber)
      .replace("<pageSize>", pageSize);

    if (reportingPeriod) {
      apiUrl = apiUrl.replace("<reportingPeriod>", reportingPeriod);
    } else {
      apiUrl = apiUrl.replace("&reportingPeriod=<reportingPeriod>", "");
    }

    return HttpUtil.get(apiUrl);
  }

  static exportBranchSubmissionsSummary(periodStart, periodEnd) {
    return HttpUtil.getExcelData(
      API_ROUTES.ASSESSMENTS.EXPORT_BRANCH_SUBMISSIONS_SUMMARY
        .replace("<periodStart>", periodStart)
        .replace("<periodEnd>", periodEnd)
    );
  }

  static createFormSection(formId, payload) {
    return HttpUtil.post(
      API_ROUTES.FORM.CREATE_FORM_SECTION.replace("<formId>", formId),
      payload
    );
  }

  static getFormUnitsOfMeasure(companyId) {
    return HttpUtil.get(
      API_ROUTES.FORM.GET_FORM_UNITS_OF_MEASURE.replace("<companyId>", companyId)
    );
  }

  static getFormDetails(formId) {
    return HttpUtil.get(
      API_ROUTES.FORM.GET_FORM_DETAILS.replace("<formId>", formId)
    );
  }

  // deleteChildren=true deletes the questions too; without it they move to the section above (form builder behaviour)
  static deleteFormSection(formId, sectionId, deleteChildren = false) {
    return HttpUtil.delete(
      API_ROUTES.FORM.DELETE_FORM_SECTION.replace("<formId>", formId)
        .replace("<sectionId>", sectionId)
        .replace(deleteChildren ? "<deleteChildren>" : "?deleteChildren=<deleteChildren>", deleteChildren ? "true" : "")
    );
  }

  static reorderFormField(formId, fieldId, payload) {
    return HttpUtil.patch(
      API_ROUTES.FORM.REORDER_FORM_FIELD.replace("<formId>", formId).replace("<fieldId>", fieldId),
      payload
    );
  }

  static reorderFormSection(formId, sectionId, payload) {
    return HttpUtil.patch(
      API_ROUTES.FORM.REORDER_FORM_SECTION.replace("<formId>", formId).replace("<sectionId>", sectionId),
      payload
    );
  }
}
